/*
  <live-view-video> — the rotating "live view" video tile that sits next to the 360 spin.

  - Shows a still (the <video> poster = MainView.webp, the hero render) immediately.
  - Lazy-loads the sparkle video (data-src) only when the tile is actually viewed, then
    plays it muted + looped with NO controls, exactly like lumalume.com.
  - Per-variant: hides the slide + thumbnail for variants that have no live-view assets,
    and swaps the poster/video on Dawn's `variant-change` pub/sub event.
  - Pauses when the tab is hidden or the tile scrolls out of view (saves CPU/battery).

  Config (inline JSON):
    {
      "activeVariantId": 123,
      "variants": { "123": { "poster": ".../MainView.webp", "video": ".../sparkle/1080.mp4" } }
    }

  Carat: every URL goes through window.BCCaratRenders.swap (assets/carat-renders.js), so
  the tile follows the carat picked in the diamond customizer (…/ct-2/ → …/ct-4/). It
  re-syncs on the `bc:carat-change` event and after every variant change.
*/
(function () {
  'use strict';

  function parseConfig(el) {
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Both the hero still and the gallery thumbnail point at FrontView.webp, which does not
  // exist for rings that predate the Aug 2026 re-render. On a 404, drop back to MainView
  // rather than leaving a broken image in the gallery. `_fellBack` guards the swap: if the
  // fallback 404s as well, re-firing `error` would put this in an infinite loop.
  function bindPosterFallback(img) {
    if (!img || img._fallbackBound) return;
    img._fallbackBound = true;
    function swap() {
      var fb = img.getAttribute('data-poster-fallback');
      if (!fb || img._fellBack || img.getAttribute('src') === fb) return;
      img._fellBack = true;
      img.setAttribute('src', fb);
    }
    img.addEventListener('error', swap);
    // The hero still is eager and sits in the initial HTML, so it can finish loading — or
    // fail — before this script runs. A listener attached after the fact never fires, so
    // catch an already-broken image here. `complete` with a zero natural width is the only
    // reliable way to spot a failed decode after the event has gone.
    if (img.complete && img.naturalWidth === 0) swap();
  }

  var LiveViewVideo = class extends HTMLElement {
    connectedCallback() {
      if (this._initialised) { this._listenCarat(); return; }
      this._initialised = true;

      var cfgEl = this.querySelector('.live-view__config');
      this.config = cfgEl ? parseConfig(cfgEl) : null;
      this.video = this.querySelector('.live-view__video');
      if (!this.config || !this.video) return;

      // The still the shopper sees until the clip is ready. It is a real <img> (not just the
      // video's poster attribute) so it paints with the page instead of waiting on the video.
      this.poster = this.querySelector('.live-view__poster');
      bindPosterFallback(this.poster);
      bindPosterFallback(this._thumbImg());

      this.activeVariantId = String(this.config.activeVariantId);
      var initial = this._rawDataFor(this.activeVariantId);
      this._shownVideo = initial ? initial.video : null; // what the SSR markup points at
      this._onVisibility = this._onVisibility.bind(this);
      this._onMotionPref = this._onMotionPref.bind(this);

      // Respect prefers-reduced-motion: users who request it should NOT get an
      // auto-playing, indefinitely-looping clip. We keep the static poster shown and
      // expose an explicit play control instead. `_userInitiated` records that the user
      // opted into motion, after which the tile behaves normally (autoplay on view, etc).
      this._userInitiated = false;
      this._mql =
        typeof window.matchMedia === 'function'
          ? window.matchMedia('(prefers-reduced-motion: reduce)')
          : null;
      this._reducedMotion = !!(this._mql && this._mql.matches);
      if (this._mql) {
        if (this._mql.addEventListener) this._mql.addEventListener('change', this._onMotionPref);
        else if (this._mql.addListener) this._mql.addListener(this._onMotionPref);
      }

      this._applyVariantVisibility(this.activeVariantId);
      this._observe();
      this._subscribeVariant();
      document.addEventListener('visibilitychange', this._onVisibility);

      // Reveal the video (fade over the still photo) the moment it actually starts playing —
      // i.e. once it's loaded enough to run smoothly. Until then the eager <img> shows the photo.
      var self = this;
      this.video.addEventListener('playing', function () {
        self.classList.add('is-playing');
        self._hidePlayControl();
      });

      this._listenCarat();
      this._syncCarat();
    }

    disconnectedCallback() {
      if (this._onCarat) document.removeEventListener('bc:carat-change', this._onCarat);
      if (this._io) this._io.disconnect();
      if (this._unsubscribe) this._unsubscribe();
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._mql) {
        if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._onMotionPref);
        else if (this._mql.removeListener) this._mql.removeListener(this._onMotionPref);
      }
      // The element may be detached by an AJAX gallery swap (innerHTML replace) while the
      // <video> is still buffering/decoding/playing in the background. Fully stop it:
      // pause, drop the src, and load() to abort the in-flight network fetch + tear down the
      // decoder, then clear the reveal class so a re-attached/cloned node starts from photo.
      this._inView = false;
      this._wantPlay = false; // a pending first-load replay must not restart a detached tile
      if (this.video) {
        try { this.video.pause(); } catch (e) {}
        this.video.removeAttribute('src');
        try { this.video.load(); } catch (e) {}
      }
      this.classList.remove('is-playing');
    }

    _rawDataFor(variantId) {
      return this.config.variants && this.config.variants[String(variantId)];
    }

    // The variant's assets, pointed at the carat the shopper picked when that carat's render
    // folder exists (see carat-renders.js); otherwise exactly the configured URLs.
    _dataFor(variantId) {
      var d = this._rawDataFor(variantId);
      var R = window.BCCaratRenders;
      if (!d || !R) return d;
      var poster = d.poster && R.swap(d.poster);
      if (poster === d.poster) return d;
      // In another carat's folder, fall back to the configured still (a real image of this
      // piece) rather than that folder's MainView, which carat folders may not have.
      return { poster: poster, posterFallback: d.poster, video: d.video && R.swap(d.video) };
    }

    /* ---------- carat (customizer weight) switching ---------- */
    _listenCarat() {
      if (!this._onCarat) this._onCarat = this._syncCarat.bind(this);
      document.removeEventListener('bc:carat-change', this._onCarat);
      document.addEventListener('bc:carat-change', this._onCarat);
    }

    // Check the chosen carat's folder for the active variant, then reload the tile if the
    // URL it should show differs from what it shows. A missing folder resolves to the
    // original URLs, so the tile never breaks.
    _syncCarat() {
      var R = window.BCCaratRenders;
      if (R && R.syncStills) R.syncStills(); // gallery render stills follow the same carat/variant
      if (!R || !this.config) return;
      var raw = this._rawDataFor(this.activeVariantId);
      if (!raw || !raw.video) return;
      var self = this;
      R.probe(raw.video, function () {
        if (!self.isConnected) return;
        var data = self._dataFor(self.activeVariantId);
        if (data && data.video !== self._shownVideo) self._onVariantChange(self.activeVariantId);
      });
    }

    _slideLi() {
      return this.closest('.product__media-item') || this.closest('li');
    }

    _thumbLi() {
      var gallery = this.closest('media-gallery');
      return gallery ? gallery.querySelector('.live-view-thumb') : null;
    }

    // The gallery thumbnail lives outside this custom element, so it is reached through the
    // gallery rather than queried locally.
    _thumbImg() {
      var li = this._thumbLi();
      return li ? li.querySelector('.live-view-thumb-img') : null;
    }

    // Show the live-view slide + thumbnail only for variants that have assets.
    _applyVariantVisibility(variantId) {
      var show = !!this._dataFor(variantId);
      [this._slideLi(), this._thumbLi()].forEach(function (el) {
        if (!el) return;
        el.classList.toggle('live-view-hidden', !show);
        if (show) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
    }

    // Point the <video> at the active variant's full-resolution sparkle clip (the original
    // 1080 MP4, served straight from the CDN — no downscaling/transform), only once. The
    // still photo stays up until this clip has loaded enough to play (the 'playing' event
    // reveals it via .is-playing), so shoppers never see a soft or half-buffered video.
    _ensureLoaded() {
      var data = this._dataFor(this.activeVariantId);
      if (!data || !data.video) return;
      if (this.video.getAttribute('src') === data.video) return;
      if (data.poster) this.video.setAttribute('poster', data.poster);
      this.video.setAttribute('src', data.video);
      this.video.load();
    }

    _play() {
      if (!this._dataFor(this.activeVariantId)) return;
      // For reduced-motion users who haven't opted in, leave the poster up (don't fetch or
      // auto-loop the clip) and surface an explicit play control instead.
      if (this._reducedMotion && !this._userInitiated) {
        this._showPlayControl();
        return;
      }
      // The FIRST clip fetch waits for window `load` + idle (timeout 1.5s) so the 1080 MP4 does
      // not compete with the page's own load; the still stays up until then, as it does while
      // any clip buffers. An explicit tap on the reduced-motion play control is never delayed.
      if (!this._firstLoadReady && !this._userInitiated) {
        this._wantPlay = true;
        this._deferFirstLoad();
        return;
      }
      this._ensureLoaded();
      var p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    // Runs once: after load + idle, allow loading and replay the most recent play request
    // (unless a later pause — scrolled away, tab hidden — cancelled it).
    _deferFirstLoad() {
      if (this._firstLoadScheduled) return;
      this._firstLoadScheduled = true;
      var self = this;
      var fire = function () {
        self._firstLoadReady = true;
        if (!self._wantPlay || !self.isConnected || document.hidden) return;
        self._wantPlay = false;
        self._play();
      };
      var afterLoad = function () {
        if ('requestIdleCallback' in window) window.requestIdleCallback(fire, { timeout: 1500 });
        else setTimeout(fire, 0);
      };
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
    }

    _pause() {
      this._wantPlay = false;
      try { this.video.pause(); } catch (e) {}
    }

    /* ---------- reduced-motion play control ---------- */
    _showPlayControl() {
      if (!this._dataFor(this.activeVariantId)) return;
      if (!this._playBtn) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'live-view__play';
        btn.setAttribute('aria-label', 'Play rotating view');
        btn.innerHTML =
          '<span class="live-view__play-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M8 5v14l11-7z"/></svg>' +
          '</span>';
        var self = this;
        btn.addEventListener('click', function () { self._userPlay(); });
        var host = this.querySelector('.live-view') || this;
        host.appendChild(btn);
        this._playBtn = btn;
      }
      this._playBtn.hidden = false;
    }

    _hidePlayControl() {
      if (this._playBtn) this._playBtn.hidden = true;
    }

    // The user explicitly asked for motion — honour it and treat the tile normally from now on.
    _userPlay() {
      this._userInitiated = true;
      this._hidePlayControl();
      this._play();
    }

    _onMotionPref(e) {
      this._reducedMotion = !!e.matches;
      if (this._reducedMotion && !this._userInitiated) {
        this._pause();
        this.classList.remove('is-playing');
        var slide = this._slideLi();
        if (slide && !slide.classList.contains('live-view-hidden')) this._showPlayControl();
      }
    }

    _observe() {
      var self = this;
      // No IntersectionObserver support: assume always in view (preserves today's behavior —
      // play immediately and let visibility/tab handling drive pause/resume).
      if (!('IntersectionObserver' in window)) { this._inView = true; this._play(); return; }
      this._io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self._inView = entry.isIntersecting;
            if (entry.isIntersecting) self._play();
            else self._pause();
          });
        },
        { threshold: 0.25 }
      );
      this._io.observe(this);
    }

    _onVisibility() {
      if (document.hidden) {
        this._pause();
      } else {
        // Returning to the tab must NOT resume a tile that's scrolled out of view — only
        // play when the tab is visible AND the tile is actually in the viewport. _play()
        // still enforces the reduced-motion / opt-in rules.
        if (!this._inView) return;
        var slide = this._slideLi();
        if (slide && !slide.classList.contains('live-view-hidden')) this._play();
      }
    }

    /* ---------- per-variant switching ---------- */
    _subscribeVariant() {
      var self = this;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (!variant) return;
        self._onVariantChange(variant.id);
      });
    }

    _onVariantChange(variantId) {
      variantId = String(variantId);
      // Karat-only switch (10k/14k/18k): the new variant resolves to the same clip and still
      // as the one on screen, so leave the tile as it is (no reload, no replay from frame 0).
      // Compared at the same moment (same carat), so any real change of URL — metal, carat,
      // a variant without a clip — still takes the full path below.
      var prev = this._dataFor(this.activeVariantId);
      var next = this._dataFor(variantId);
      if (prev && next && variantId !== this.activeVariantId && next.video &&
          next.video === this._shownVideo && next.video === prev.video &&
          next.poster === prev.poster && next.posterFallback === prev.posterFallback) {
        this.activeVariantId = variantId;
        this._applyVariantVisibility(variantId);
        this._syncCarat();
        return;
      }
      this.activeVariantId = variantId;
      this._applyVariantVisibility(variantId);
      this.classList.remove('is-playing');

      var data = this._dataFor(variantId);
      this._shownVideo = data ? data.video : null;
      this._pause();
      this.video.removeAttribute('src');
      if (data) {
        if (data.poster) this.video.setAttribute('poster', data.poster);
        // Keep the visible still on the same variant as the clip. Without this the shopper
        // switches metal and the tile keeps showing the metal it first rendered with until
        // the video loads over it. `_fellBack` is cleared so the new variant gets its own
        // chance at FrontView instead of inheriting the previous one's fallback.
        if (this.poster && data.poster) {
          this.poster._fellBack = false;
          if (data.posterFallback) this.poster.setAttribute('data-poster-fallback', data.posterFallback);
          this.poster.setAttribute('src', data.poster);
        }
        // The gallery thumbnail shows the same still, so it follows too.
        var thumb = this._thumbImg();
        if (thumb && data.poster && thumb.getAttribute('src') !== data.poster) {
          thumb._fellBack = false;
          if (data.posterFallback) thumb.setAttribute('data-poster-fallback', data.posterFallback);
          thumb.setAttribute('src', data.poster);
        }
        // Only (re)load + play if this tile is the visible hero or on screen.
        var slide = this._slideLi();
        if ((slide && slide.classList.contains('is-active')) || this._inView) this._play();
      }
      // A new variant has its own render folder — make sure it shows the chosen carat too.
      this._syncCarat();
    }
  };

  if (!customElements.get('live-view-video')) {
    customElements.define('live-view-video', LiveViewVideo);
  }
})();
